import { Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { environment } from '@environments/environment';

import { HomeComponent } from './modules/dashboard/_home/home.component';
import { type LayoutRouteData } from './services/layout.service';
import { Constants } from './shared/constants';
import { userRoleGuard } from './shared/guards/user-role.guard';
import { customTitleResolver } from './shared/resolvers/customTitle.resolver';

/**
 * @ignore in coverage
 */
const userRoles = Constants.roles;

/**
 * @ignore in coverage
 */
const standardRoutes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        component: HomeComponent,
        title: 'Scope Hub',
        data: {
            breadcrumb: $localize`:@@page-breadcrumb-home:Home`
        }
    },
    {
        path: 'privacy-policy',
        loadComponent: () => import( './pages/privacy-policy/privacy-policy.component' )
            .then( ( c ) => c.PrivacyPolicyComponent ),
        title: customTitleResolver,
        data: {
            breadcrumb: $localize`:@@page-breadcrumb-privacy-policy:Privacy Policy`
        }
    },
    {
        path: 'accessibility-statement',
        loadComponent: () => import( './pages/a11y-statement/a11y-statement.component' )
            .then( ( c ) => c.A11yStatementComponent ),
        title: customTitleResolver,
        data: {
            breadcrumb: $localize`: @@page-breadcrumb-accessibility-statement:Accessibility Statement`
        }
    },
    {
        path: '403',
        loadComponent: () => import( './pages/not-allowed/not-allowed.component' )
            .then( ( c ) => c.NotAllowedComponent ),
        title: customTitleResolver,
        data: {
            title: $localize`: @@page-title-403:Not allowed`,
            breadcrumb: $localize`: @@page-breadcrumb-403:Not allowed`
        }
    },
    {
        path: '404',
        loadComponent: () => import( './pages/not-found/not-found.component' )
            .then( ( c ) => c.NotFoundComponent ),
        title: customTitleResolver,
        data: {
            title: $localize`: @@page-title-404:Page not found`,
            breadcrumb: $localize`: @@page-breadcrumb-404:Page not found`
        }
    },
    {
        path: '**',
        loadComponent: () => import( './pages/not-found/not-found.component' )
            .then( ( c ) => c.NotFoundComponent ),
        title: customTitleResolver,
        data: {
            title: $localize`: @@page-title-404:Page not found`,
            breadcrumb: $localize`: @@page-breadcrumb-404:Page not found`
        }
    }
];

/**
 * @ignore in coverage
 */
const getAppRoutes = (): Routes => {
    const dynamicRoutes: Routes = [];

    if ( environment.optionalModules.showStyleGuide ) {
        const route = {
            path: 'styleguide',
            loadComponent: () => import( './pages/styleguide/styleguide.component' ).then( ( c ) => c.StyleGuideComponent ),
            title: customTitleResolver,
            data: {
                title: 'StyleGuide',
                breadcrumb: 'StyleGuide'
            }
        };
        dynamicRoutes.push( route );
    }

    const userAccountRoute = {
        path: 'user-account',
        canActivate: [ MsalGuard ],
        loadChildren: () => import( './modules/user-account/user-account.routes' ).then( ( r ) => r.USER_ACCOUNT_ROUTES ),
        title: customTitleResolver,
        data: {
            title: $localize`: @@page-title-account:User Account`,
            breadcrumb: {
                label: $localize`: @@page-breadcrumb-account:User Account`,
                disable: true
            }
        }
    };
    dynamicRoutes.push( userAccountRoute );

    const tenantsRoute = {
        path: 'tenants',
        canActivate: [ MsalGuard ],
        loadChildren: () => import( './modules/tenants/tenants.routes' ).then( ( r ) => r.TENANTS_ROUTES ),
        title: customTitleResolver,
        data: {
            title: $localize`: @@page-title-tenants:Tenants`,
            breadcrumb: $localize`: @@page-breadcrumb-tenants:Tenants`
        }
    };
    dynamicRoutes.push( tenantsRoute );

    const instancesRoute = {
        path: 'instances',
        canActivate: [ MsalGuard ],
        loadChildren: () => import( './modules/instances/instances.routes' ).then( ( r ) => r.INSTANCES_ROUTES ),
        title: customTitleResolver,
        data: {
            title: $localize`: @@page-title-instances:Instances`,
            breadcrumb: $localize`: @@page-breadcrumb-instances:Instances`
        }
    };
    dynamicRoutes.push( instancesRoute );

    const scopeCommunityRoute = {
        path: 'scope-community',
        loadChildren: () => import( './modules/scope-community/scope-community.routes' ).then( ( r ) => r.SCOPE_COMMUNITY_ROUTES ),
        title: customTitleResolver,
        data: {
            title: $localize`: @@page-title-scope-community:Community`,
            breadcrumb: $localize`: @@page-breadcrumb-scope-community:Community`
        }
    };
    dynamicRoutes.push( scopeCommunityRoute );

    const toolsRoute = {
        path: 'tools',
        loadChildren: () =>
            import( './modules/tools/tools.routes' ).then( ( r ) => r.TOOLS_ROUTES ),
        title: customTitleResolver,
        data: {
            title: $localize`: @@page-title-tools:Tools`,
            breadcrumb: $localize`: @@page-breadcrumb-tools:Tools`
        }
    };
    dynamicRoutes.push( toolsRoute );

    if ( environment.optionalModules.showServiceModules ) {
        const serviceRoute = {
            path: 'service',
            canActivate: [ MsalGuard, userRoleGuard ],
            loadChildren: () =>
                import( './modules/service/service.routes' ).then( ( r ) => r.SERVICE_ROUTES ),
            title: customTitleResolver,
            data: {
                allowedRoles: [ userRoles.RSI_Super_Administrator ],
                title: $localize`: @@page-title-service:Service`,
                breadcrumb: $localize`: @@page-breadcrumb-service:Service`
            }
        };
        dynamicRoutes.push( serviceRoute );
    }

    const route = {
        path: 'administration',
        canActivate: [ MsalGuard ],
        loadChildren: () =>
            import( './modules/administration/administration.routes' ).then( ( r ) => r.ADMINISTRATION_ROUTES ),
        title: customTitleResolver,
        data: {
            title: $localize`: @@page-title-administration:Administration`,
            breadcrumb: $localize`: @@page-breadcrumb-administration:Administration`
        }
    };
    dynamicRoutes.push( route );

    if ( environment.optionalModules.showContainerTracking ) {
        const route = {
            path: 'tracking',
            loadChildren: () => import( './modules/tracking/tracking-routing.routes' ).then( ( r ) => r.TRACKING_ROUTES ),
            data: {
                title: $localize`: @@page-title-tracking:Tracking`,
                layout: 'minimal',
                breadcrumb: $localize`: @@page-breadcrumb-tracking:Tracking`
            } satisfies LayoutRouteData
        };
        dynamicRoutes.push( route );
    }

    // NOTE: dynamic routes need to be handled first
    return [ ...dynamicRoutes, ...standardRoutes ];
};

/**
 * @ignore in coverage
 */
export const APP_ROUTES: Routes = getAppRoutes();
